'use no memo';

import { AssetInstance, NULL_TIME } from '@kalos/kalos-rpc';
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  DateInput,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormItem,
  Skeleton,
  toast,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kalos/ui';
import { CalendarIcon } from '@radix-ui/react-icons';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { addYears, format, parseISO } from 'date-fns';
import { type FC, useCallback, useMemo, useState } from 'react';

import { DialogOrSheet } from '../../../components/DialogOrSheet';
import { FieldConfirmSheet } from '../../../components/FieldConfirmSheet';
import { useGetLoadingColumns } from '../../../components/utils';
import { ROWS_PER_PAGE } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import {
  useAssetInstanceUpdateMutation,
  useBatchGetAssetInstanceQuery,
} from '../../../hooks/react-query/useAssetInstanceQuery';
import { useToolFundCatalogQuery } from '../../../hooks/react-query/useToolFundCatalogQuery';
import { useFieldLayout } from '../../../hooks/useMedia';
import { timestamp } from '../../../tools/helpers';

const LOADING_ASSETS: AssetInstance[] = Array.from({ length: 5 }, (_, idx) =>
  AssetInstance.create({
    id: idx,
    itemId: 0,
    fulfillmentId: 0,
    assetTag: '',
    currentLocationId: 0,
  }),
);

const staticAssetsArray: AssetInstance[] = [];

const getDefaultVestingDate = (asset: AssetInstance) => {
  if (!asset.vestingDate) return undefined;

  const ts = Number(asset.vestingDate);
  if (isNaN(ts)) return undefined;

  const date = new Date(ts);
  return isNaN(date.getTime()) ? undefined : date;
};

export const UpdateVestingDateDialog = ({
  asset,
  onOpenChange,
}: {
  asset: AssetInstance;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [vestingDate, setVestingDate] = useState<Date | undefined>(getDefaultVestingDate(asset));

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
      if (!open) {
        setVestingDate(getDefaultVestingDate(asset));
      }
    },
    [onOpenChange, asset],
  );

  const updateMutation = useAssetInstanceUpdateMutation();
  const isFieldLayout = useFieldLayout();

  const handleUpdate = async () => {
    if (!vestingDate || isNaN(vestingDate.getTime())) {
      toast({
        title: 'Error',
        description: 'Please select a valid date',
        variant: 'destructive',
      });
      return;
    }

    try {
      const date = new Date(vestingDate);
      date.setHours(23, 59, 59, 999);

      await updateMutation.mutateAsync(
        AssetInstance.create({
          id: asset.id,
          vestingDate: timestamp(false, date),
          fieldMask: ['VestingDate'],
        }),
      );

      toast({
        title: 'Success',
        description: 'Vesting date updated successfully',
        variant: 'success',
      });

      handleOpenChange(false);
    } catch (err) {
      console.error('Failed to update vesting date:', err);
      toast({
        title: 'Error',
        description: 'Failed to update vesting date',
        variant: 'destructive',
      });
    }
  };

  const dateField = (
    <FormItem>
      <DateInput
        value={vestingDate}
        onChange={(date) => {
          if (date && !isNaN(date.getTime())) {
            setVestingDate(date);
          }
        }}
        mode="day"
        disableManualInput
        maxDate={new Date(new Date().getFullYear() + 10, 11, 31)}
      />
    </FormItem>
  );
  const updateDisabled = !vestingDate || isNaN(vestingDate.getTime()) || updateMutation.isPending;

  // Phones and tablets: a sheet, which can't be closed while it saves.
  if (isFieldLayout) {
    return (
      <DialogOrSheet
        open
        onOpenChange={handleOpenChange}
        title="Update Vesting Date"
        description="Set a new vesting date for this tool."
        isBusy={updateMutation.isPending}
      >
        <div className="space-y-4">
          {dateField}
          <Button
            className="w-full"
            onClick={handleUpdate}
            disabled={updateDisabled}
            isLoading={updateMutation.isPending}
          >
            Update
          </Button>
        </div>
      </DialogOrSheet>
    );
  }

  return (
    <Dialog open={true} onOpenChange={handleOpenChange} modal>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Vesting Date</DialogTitle>
          <DialogDescription>Set a new vesting date for this tool.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {dateField}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={updateDisabled}
              isLoading={updateMutation.isPending}
            >
              Update
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MarkAsReceivedDialog = ({
  asset,
  onOpenChange,
}: {
  asset: AssetInstance;
  onOpenChange?: (open: boolean) => void;
}) => {
  const updateMutation = useAssetInstanceUpdateMutation();
  const isFieldLayout = useFieldLayout();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  const handleMarkAsReceived = async () => {
    try {
      const now = new Date();
      const formattedDate = format(now, 'yyyy-MM-dd HH:mm:ss');

      await updateMutation.mutateAsync(
        AssetInstance.create({
          id: asset.id,
          acquisitionDate: formattedDate,
          vestingDate: format(addYears(now, 3), 'yyyy-MM-dd HH:mm:ss'),
          fieldMask: ['AcquisitionDate', 'VestingDate'],
        }),
      );

      toast({
        title: 'Success',
        description: 'Tool marked as received',
        variant: 'success',
      });

      handleOpenChange(false);
    } catch (err) {
      console.error('Failed to mark tool as received:', err);
      toast({
        title: 'Error',
        description: 'Failed to mark tool as received',
        variant: 'destructive',
      });
    }
  };

  // Phones and tablets: a confirmation sheet whose button names the action.
  if (isFieldLayout) {
    return (
      <FieldConfirmSheet
        open
        onOpenChange={handleOpenChange}
        title="Mark Tool as Received"
        description="Are you sure you want to mark this tool as received? This will set the acquisition date to today."
        confirmLabel="Mark as Received"
        onConfirm={handleMarkAsReceived}
        isBusy={updateMutation.isPending}
      />
    );
  }

  return (
    <Dialog open={true} onOpenChange={handleOpenChange} modal>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark Tool as Received</DialogTitle>
          <DialogDescription>
            Are you sure you want to mark this tool as received? This will set the acquisition date
            to today.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleMarkAsReceived}
            disabled={updateMutation.isPending}
            isLoading={updateMutation.isPending}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export type ToolFundEmployeeAssetsTableProps = {
  userId: number;
  /** Scroll/constrain height when embedded (e.g. employee dashboard card). */
  compact?: boolean;
};

/** Tools inventory table + pagination + mark-received / admin vesting actions (same behavior as Tool Log → Tools tab). */
export const ToolFundEmployeeAssetsTable: FC<ToolFundEmployeeAssetsTableProps> = ({
  userId,
  compact = false,
}) => {
  const loggedUser = useAuth().user;
  const [page, setPage] = useState(0);
  const [vestingDialogOpen, setVestingDialogOpen] = useState<AssetInstance | null>(null);
  const [receivedDialogOpen, setReceivedDialogOpen] = useState<AssetInstance | null>(null);

  const isToolFundAdmin = loggedUser.permissionGroups.some((perm) => perm.name === 'ToolFundAdmin');
  const isOwnLog = loggedUser.id === userId;

  const { data: catalogData } = useToolFundCatalogQuery({
    filter: { pageNumber: -1 },
    select(data) {
      return new Map(data.results.map((item) => [item.id, item]));
    },
  });

  const { data: assetsData, isLoading: isLoadingAssets } = useBatchGetAssetInstanceQuery({
    filters: {
      currentUserId: userId,
      pageNumber: page,
      includeAssociatedFulfillment: true,
    },
  });

  const assetColumns: ColumnDef<AssetInstance>[] = useMemo(
    () => [
      {
        id: 'toolName',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            hideVisibilityToggle
            title={
              <>
                <span className="sm:hidden">Tool</span>
                <span className="hidden sm:inline">Tool name</span>
              </>
            }
          />
        ),
        cell: ({ row }) => {
          const asset = row.original;
          const catalogItem = catalogData?.get(asset.itemId);
          const text =
            asset.fulfillmentItem?.actualItemName ||
            catalogItem?.name ||
            asset.assetTag ||
            'Unknown Tool';
          return <span className="block min-w-0 break-words leading-snug">{text}</span>;
        },
      },
      {
        id: 'acquisitionDate',
        header: ({ column }) => (
          <DataTableColumnHeader
            hideVisibilityToggle
            column={column}
            title={
              <>
                <span className="sm:hidden">Acquired</span>
                <span className="hidden sm:inline">Acquisition date</span>
              </>
            }
          />
        ),
        cell: ({ row }) => {
          const acquisitionDate = row.original.acquisitionDate;
          if (!acquisitionDate || acquisitionDate === NULL_TIME || acquisitionDate === '') {
            return <span className="block min-w-0">N/A</span>;
          }
          return (
            <span className="block min-w-0 whitespace-nowrap text-xs tabular-nums sm:text-sm">
              {format(parseISO(acquisitionDate), 'MM/dd/yy')}
            </span>
          );
        },
      },
      {
        id: 'toolReceived',
        header: ({ column }) => (
          <DataTableColumnHeader
            hideVisibilityToggle
            column={column}
            title={
              <>
                <span className="sm:hidden">Rcvd</span>
                <span className="hidden sm:inline">Received?</span>
              </>
            }
          />
        ),
        cell: ({ row }) => {
          const acquisitionDate = row.original.acquisitionDate;
          const isReceived =
            acquisitionDate && acquisitionDate !== NULL_TIME && acquisitionDate !== '';
          return <span className="block min-w-0">{isReceived ? 'Yes' : 'No'}</span>;
        },
      },
      {
        id: 'vestingDate',
        header: ({ column }) => (
          <DataTableColumnHeader
            hideVisibilityToggle
            column={column}
            title={
              <>
                <span className="sm:hidden">Vest</span>
                <span className="hidden sm:inline">Vesting date</span>
              </>
            }
          />
        ),
        cell: ({ row }) => {
          const vestingDate = row.original.vestingDate;
          if (!vestingDate || vestingDate === NULL_TIME || vestingDate === '') {
            return <span className="block min-w-0">N/A</span>;
          }

          const date = parseISO(vestingDate);
          return (
            <span className="block min-w-0 whitespace-nowrap text-xs tabular-nums sm:text-sm">
              {isNaN(date.getTime()) ? '—' : format(date, 'MM/dd/yy')}
            </span>
          );
        },
      },
      {
        id: 'comments',
        header: ({ column }) => (
          <DataTableColumnHeader
            hideVisibilityToggle
            column={column}
            title={
              <>
                <span className="sm:hidden">Notes</span>
                <span className="hidden sm:inline">Comments</span>
              </>
            }
          />
        ),
        cell: ({ row }) => {
          const asset = row.original;
          const fulfillmentItem = asset.fulfillmentItem;
          if (!fulfillmentItem) {
            return <span className="block min-w-0">—</span>;
          }
          const text = fulfillmentItem.notes || '-';
          return <span className="block min-w-0 break-words leading-snug">{text}</span>;
        },
      },
      {
        id: 'actions',
        cell: ({ row }) => {
          const asset = row.original;
          return (
            <div className="flex min-w-0 flex-wrap justify-end gap-1 sm:gap-2">
              {isToolFundAdmin && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setVestingDialogOpen(asset)}
                      >
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Update Vesting Date</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {isOwnLog && (asset.acquisitionDate == NULL_TIME || asset.acquisitionDate == '') && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setReceivedDialogOpen(asset)}
                      >
                        ✓
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Mark as Received</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        },
      },
    ],
    [catalogData, isToolFundAdmin, isOwnLog],
  );

  const loadingAssetsColumns = useGetLoadingColumns(assetColumns);

  const assetsTable = useReactTable<AssetInstance>({
    data: isLoadingAssets ? LOADING_ASSETS : assetsData?.results ?? staticAssetsArray,
    columns: isLoadingAssets ? loadingAssetsColumns : assetColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
  });

  const toolsPageCount = assetsData?.totalCount
    ? Math.ceil(assetsData.totalCount / ROWS_PER_PAGE)
    : 0;

  return (
    <div className="space-y-4">
      {isLoadingAssets ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <>
          {toolsPageCount > 1 ? (
            <div className="flex items-center justify-end pr-1 sm:pr-4">
              <div className="flex w-full max-w-max flex-wrap items-center justify-between gap-4">
                <DataTablePagination
                  currentPage={page}
                  setPage={setPage}
                  pageCount={toolsPageCount}
                />
                {assetsData?.totalCount && assetsData.totalCount > 0 ? (
                  <p className="text-muted-foreground text-sm">
                    Total Entries: {assetsData.totalCount}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          <div
            className={
              compact
                ? 'max-h-[min(20rem,45vh)] overflow-x-auto overflow-y-auto'
                : 'min-w-0 overflow-x-auto'
            }
          >
            <div className="w-full min-w-0">
              <DataTable
                table={assetsTable}
                tableCellClassName="min-w-0 break-words px-1 py-1.5 align-top text-[0.7rem] leading-snug sm:px-2 sm:py-2 sm:text-xs"
                alwaysCard
              />
            </div>
          </div>
        </>
      )}

      {vestingDialogOpen ? (
        <UpdateVestingDateDialog
          asset={vestingDialogOpen}
          onOpenChange={(open) => !open && setVestingDialogOpen(null)}
        />
      ) : null}
      {receivedDialogOpen ? (
        <MarkAsReceivedDialog
          asset={receivedDialogOpen}
          onOpenChange={(open) => !open && setReceivedDialogOpen(null)}
        />
      ) : null}
    </div>
  );
};
